import apiClient from "@/api/apiClient";
import { updateCase } from "@/api/resources/cases/api";
import { updateMaintenance } from "@/api/resources/maintenances/api";
import {
  createGlobalFilterArgs,
  createPaginatedRpcSupabaseQuery,
  createRpcQuery,
  spawnCreateApiFunction,
  spawnDeleteApiFunction,
  spawnGetRpcApiFunction,
  spawnListRpcInApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
  type ApiViewResourceConfig,
} from "@/utils/api";

import type {
  BulkAssignServiceJobCasesParams,
  BulkAssignServiceJobMaintenancesParams,
  SearchServiceJobsProps,
  ServiceJobDetailCorrected,
} from "@/api/resources/serviceJobs/types";
import type { ArgsSetServiceJobPrimaryReason } from "@/supabase.types";

const table = "service_jobs";

const query = `
                *,
                public_id,
                cases!cases_to_service_jobs(
                  *,
                  public_id,
                  device:devices(*, device_tags(*), manufacturer:manufacturers(*), model:device_models(*)),
                  parts:cases_to_parts(id),
                  cases_to_maintenances(maintenance_id),
                  cases_to_device_data_triggers(device_data_trigger_id),
                  case_tags(*)
                ),
                maintenances!maintenances_to_service_jobs(
                  *,
                  public_id,
                  planned_date,
                  planned_date_until,
                  parent_maintenances:maintenances_to_maintenances!m_to_m_maintenance_parent_id_fkey(maintenance_parent_id),
                  child_maintenances:maintenances_to_maintenances!m_to_m_maintenance_child_id_fkey(maintenance_child_id),
                  cases_to_maintenances(case_id),
                  assignee:user_profiles!maintenances_assignee_id_fkey(*),
                  maintenances_to_devices(device_id, device:devices(id, device_tags(id))),
                  maintenance_items(status),
                  maintenance_strategy:maintenance_strategies(
                    title,
                    is_active,
                    maintenance_strategies_to_devices(device_id)
                  ),
                  maintenance_tags(*)
                ),
                assignees:user_profiles!users_to_service_jobs(*)
                `;

export const apiConfigServiceJobs: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["name", "public_id", "description", "address_1", "address_2", "zipcode", "city", "country"],
  sortables: ["name", "public_id", "updated_at", "created_at", "start_at", "end_at"],
};

export const getServiceJob = spawnGetRpcApiFunction({ rpc: "get_service_job_detail" });
export const getServiceJobsByIds = spawnListRpcInApiFunction({
  rpc: "list_service_job_details",
});
export const createServiceJob = spawnCreateApiFunction({ table, query });
export const updateServiceJob = spawnUpdateApiFunction({ table, query });
export const deleteServiceJob = spawnDeleteApiFunction({ table });

export const setServiceJobPrimaryReason = async (args: ArgsSetServiceJobPrimaryReason) => {
  const { data, error } = await apiClient.rpc("set_service_job_primary_reason", args);
  if (error) throw error;

  return data;
};

export const bulkAssignServiceJobCases = async ({ assignee_id, case_ids }: BulkAssignServiceJobCasesParams) => {
  const requests = case_ids.map((caseId) =>
    updateCase({
      id: caseId,
      values: {
        assignee_id,
      },
    }),
  );
  await Promise.all(requests);
};

export const bulkAssignServiceJobMaintenances = async ({
  assignee_id,
  maintenance_ids,
}: BulkAssignServiceJobMaintenancesParams) => {
  const requests = maintenance_ids.map((maintenanceId) =>
    updateMaintenance({
      id: maintenanceId,
      values: {
        assignee_id,
      },
    }),
  );
  await Promise.all(requests);
};

const view = "service_job_details";
export const queryView = "*";
export const apiConfigServiceJobDetailsView: ApiViewResourceConfig<typeof view, typeof queryView> = {
  view,
  query: queryView,
  searchables: ["name", "public_id", "description", "address_1", "address_2", "zipcode", "city", "country"],
  sortables: ["name", "public_id", "updated_at", "created_at", "start_at", "end_at"],
};

export async function getPaginatedServiceJobs({
  dataTableOptions,
  customFilters,
  customOrFilters,
  globalFilters,
}: SearchServiceJobsProps) {
  const _filters = createGlobalFilterArgs(globalFilters, dataTableOptions.search);

  const { data, count, error } = await createPaginatedRpcSupabaseQuery<"list_service_job_details">({
    baseOptions: {
      rpcQuery: createRpcQuery(
        "list_service_job_details",
        {
          _filters,
        },
        { count: "estimated" },
      ),
      searchables: apiConfigServiceJobDetailsView.searchables,
      sortables: apiConfigServiceJobDetailsView.sortables,
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data: data as ServiceJobDetailCorrected[], count };
}
